/**
 * TassonomiaDialog — manutenzione del vocabolario: tag per faccetta, proposte da
 * approvare, FUSIONE dei doppioni.
 *
 * La fusione è la funzione più importante di questa schermata: prima o poi due
 * tag per la stessa cosa nascono comunque, e senza un modo di unirli la
 * tassonomia degenera in sei mesi. Unendo, l'etichetta perdente diventa un alias
 * del vincente → quel modo di dirlo non genererà mai più un tag nuovo.
 *
 * Doc canonico: docs/01-ARCHITECTURE/RETE-CONTATTI-MODEL.md
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Paper, Stack, TextField, Tooltip,
  Typography, alpha,
} from '@mui/material';
import { Add, CallMerge, CheckCircle, Delete, Tune } from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { FaccetteEditor } from './FaccetteEditor';
import { COLORE_FACCETTA_DEFAULT } from './labels';
import { risolviTag, trovaTagSimili } from '@shared/rete/reteTassonomia';
import {
  attivaTag, eliminaTag, fondiTag, saveTag,
} from '../../services/reteContattiService';
import type { FaccettaRete, TagRete } from '../../types/reteContatti';

interface Props {
  open: boolean;
  faccette: FaccettaRete[];
  tag: TagRete[];
  onClose: () => void;
  /** Tassonomia cambiata: il chiamante ricarica tag, faccette e persone. */
  onCambiata: () => Promise<void>;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const TassonomiaDialog: React.FC<Props> = ({
  open, faccette, tag, onClose, onCambiata, showSnack,
}) => {
  const [lavorando, setLavorando] = useState<string | null>(null);
  const [mostraFaccette, setMostraFaccette] = useState(false);

  // Creazione manuale
  const [nuovaEtichetta, setNuovaEtichetta] = useState('');
  const [nuovaFaccetta, setNuovaFaccetta] = useState('');

  // Fusione: due tag selezionati
  const [fusione, setFusione] = useState<{ vincente: string; perdente: string }>({
    vincente: '', perdente: '',
  });

  const proposti = useMemo(() => tag.filter((t) => t.stato === 'proposto'), [tag]);
  const attivi = useMemo(() => tag.filter((t) => t.stato === 'attivo'), [tag]);

  /** Quasi-duplicati di ciò che si sta scrivendo: avvisa PRIMA di creare. */
  const simili = useMemo(
    () => (nuovaEtichetta.trim().length >= 3 ? trovaTagSimili(nuovaEtichetta, attivi) : []),
    [nuovaEtichetta, attivi],
  );
  const esatto = useMemo(
    () => (nuovaEtichetta.trim() ? risolviTag(nuovaEtichetta, tag) : null),
    [nuovaEtichetta, tag],
  );

  const esegui = async (chiave: string, azione: () => Promise<void>, messaggio: string) => {
    setLavorando(chiave);
    try {
      await azione();
      await onCambiata();
      showSnack(messaggio, 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Operazione non riuscita', 'error');
    } finally {
      setLavorando(null);
    }
  };

  const creaTag = () =>
    esegui(
      'crea',
      async () => {
        await saveTag({
          etichetta: nuovaEtichetta.trim(),
          faccetta: nuovaFaccetta,
          stato: 'attivo',
          alias: [],
        });
        setNuovaEtichetta('');
      },
      'Tag creato',
    );

  const eseguiFusione = () => {
    const vincente = tag.find((t) => t.id === fusione.vincente);
    const perdente = tag.find((t) => t.id === fusione.perdente);
    if (!vincente || !perdente) return;
    return esegui(
      'fondi',
      async () => {
        const esito = await fondiTag({
          tag_vincente_id: fusione.vincente,
          tag_perdente_id: fusione.perdente,
        });
        setFusione({ vincente: '', perdente: '' });
        showSnack(
          `Fusi: ${esito.persone_aggiornate} schede aggiornate, «${perdente.etichetta}» ora è un sinonimo`,
          'success',
        );
      },
      'Fusione completata',
    );
  };

  return (
    <Dialog open={open} onClose={lavorando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600, pb: 0.5 }}>
        Tag e faccette
        <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, fontFamily: tokens.font.body }}>
          I tag si combinano tra faccette diverse: pochi tag ben scelti coprono
          molte più sfumature di tanti tag simili tra loro.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* PROPOSTE DA APPROVARE ---------------------------------------- */}
          {proposti.length > 0 && (
            <Box>
              <Titolo testo={`Proposte da approvare (${proposti.length})`} />
              <Stack spacing={1}>
                {proposti.map((t) => {
                  const f = faccette.find((x) => x.id === t.faccetta);
                  return (
                    <Paper
                      key={t.id}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderColor: alpha(tokens.color.warning, 0.4),
                        bgcolor: alpha(tokens.color.warning, 0.04),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t.etichetta}
                            </Typography>
                            <SoftChip label={f?.etichetta ?? t.faccetta} tone="default" />
                          </Stack>
                          {t.motivo_proposta && (
                            <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                              {t.motivo_proposta}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          size="small" variant="contained"
                          startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                          disabled={lavorando !== null}
                          onClick={() => esegui(`attiva-${t.id}`, () => attivaTag(t.id), 'Tag attivato')}
                          sx={{ textTransform: 'none' }}
                        >
                          attiva
                        </Button>
                        <Tooltip title="Elimina la proposta">
                          <IconButton
                            size="small" disabled={lavorando !== null}
                            onClick={() => esegui(
                              `elimina-${t.id}`,
                              async () => { await eliminaTag(t.id); },
                              'Proposta eliminata',
                            )}
                          >
                            <Delete fontSize="small" sx={{ color: tokens.color.mist }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* TAG PER FACCETTA --------------------------------------------- */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Titolo testo="Tag attivi" />
              <Button
                size="small" startIcon={<Tune sx={{ fontSize: 16 }} />}
                onClick={() => setMostraFaccette((v) => !v)}
                sx={{ textTransform: 'none', color: tokens.color.ash }}
              >
                {mostraFaccette ? 'nascondi faccette' : 'gestisci faccette'}
              </Button>
            </Stack>

            {mostraFaccette && (
              <Box sx={{ mb: 2 }}>
                <FaccetteEditor
                  faccette={faccette}
                  onCambiata={onCambiata}
                  showSnack={showSnack}
                />
                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {faccette.length === 0 ? (
              <Alert severity="info">
                Nessuna faccetta configurata: creane almeno una da «gestisci faccette».
              </Alert>
            ) : (
              <Stack spacing={2}>
                {faccette.map((f) => {
                  const suoi = attivi
                    .filter((t) => t.faccetta === f.id)
                    .sort((a, b) => (b.conteggio_uso ?? 0) - (a.conteggio_uso ?? 0));
                  const colore = f.colore || COLORE_FACCETTA_DEFAULT;
                  return (
                    <Box key={f.id}>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography
                          sx={{
                            fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
                            textTransform: 'uppercase', color: colore,
                          }}
                        >
                          {f.etichetta}
                        </Typography>
                        {!f.consenti_proposte_ai && (
                          <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                            (chiusa: l'AI non può proporne di nuovi)
                          </Typography>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                        {suoi.length === 0 && (
                          <Typography variant="caption" sx={{ color: tokens.color.mist, fontStyle: 'italic' }}>
                            nessun tag
                          </Typography>
                        )}
                        {suoi.map((t) => (
                          <Tooltip
                            key={t.id}
                            title={
                              (t.alias ?? []).length
                                ? `sinonimi: ${(t.alias ?? []).join(', ')}`
                                : 'nessun sinonimo registrato'
                            }
                          >
                            <Chip
                              label={`${t.etichetta} · ${t.conteggio_uso ?? 0}`}
                              size="small"
                              onDelete={
                                lavorando !== null
                                  ? undefined
                                  : () => esegui(
                                    `elimina-${t.id}`,
                                    async () => { await eliminaTag(t.id); },
                                    'Tag eliminato',
                                  )
                              }
                              sx={{
                                height: 28, fontSize: '0.76rem',
                                bgcolor: alpha(colore, 0.1), color: colore,
                                border: `1px solid ${alpha(colore, 0.3)}`,
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* CREA TAG ------------------------------------------------------ */}
          <Box>
            <Titolo testo="Aggiungi un tag" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                label="Etichetta" size="small" fullWidth
                value={nuovaEtichetta}
                onChange={(e) => setNuovaEtichetta(e.target.value)}
                placeholder="minuscolo, forma singolare"
              />
              <TextField
                label="Faccetta" select size="small"
                value={nuovaFaccetta}
                onChange={(e) => setNuovaFaccetta(e.target.value)}
                sx={{ minWidth: 190 }}
              >
                {faccette.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.etichetta}</MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained" startIcon={<Add />}
                disabled={
                  lavorando !== null || !nuovaEtichetta.trim() || !nuovaFaccetta || !!esatto
                }
                onClick={creaTag}
                sx={{ textTransform: 'none', minHeight: 40 }}
              >
                aggiungi
              </Button>
            </Stack>

            {esatto && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Esiste già: «{esatto.etichetta}»
                {esatto.stato === 'proposto' ? ' (in attesa di approvazione)' : ''}.
              </Alert>
            )}
            {!esatto && simili.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Simili già presenti: {simili.map((s) => `«${s.tag.etichetta}»`).join(', ')} — se è
                la stessa cosa, usa quello e non crearne un altro.
              </Alert>
            )}
          </Box>

          <Divider />

          {/* FUSIONE ------------------------------------------------------- */}
          <Box>
            <Titolo testo="Unisci due tag doppi" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                label="Tieni questo" select size="small" fullWidth
                value={fusione.vincente}
                onChange={(e) => setFusione((p) => ({ ...p, vincente: e.target.value }))}
              >
                {attivi.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.etichetta} · {t.conteggio_uso ?? 0}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Assorbi questo" select size="small" fullWidth
                value={fusione.perdente}
                onChange={(e) => setFusione((p) => ({ ...p, perdente: e.target.value }))}
              >
                {tag
                  .filter((t) => t.id !== fusione.vincente)
                  .map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.etichetta} · {t.conteggio_uso ?? 0}
                    </MenuItem>
                  ))}
              </TextField>
              <Button
                variant="contained" color="warning"
                startIcon={
                  lavorando === 'fondi'
                    ? <CircularProgress size={16} color="inherit" />
                    : <CallMerge />
                }
                disabled={
                  lavorando !== null || !fusione.vincente || !fusione.perdente ||
                  fusione.vincente === fusione.perdente
                }
                onClick={eseguiFusione}
                sx={{ textTransform: 'none', minHeight: 40, flexShrink: 0 }}
              >
                unisci
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.75 }}>
              Le schede col tag assorbito passano a quello tenuto, e la sua etichetta
              diventa un sinonimo: non verrà più riproposto come tag nuovo.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={lavorando !== null} variant="contained">
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Titolo: React.FC<{ testo: string }> = ({ testo }) => (
  <Typography
    sx={{
      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: tokens.color.mist, mb: 1,
    }}
  >
    {testo}
  </Typography>
);
